import React from 'react';

const About: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 text-white py-20 md:py-32">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              About Us
            </h1>
            <div className="w-24 h-1 bg-blue-300 mx-auto mb-8"></div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Image Section */}
              <div className="order-2 lg:order-1">
                <div className="relative">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 shadow-xl">
                    <div className="bg-white rounded-xl p-6 shadow-lg">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-lg">G</span>
                          </div>
                          <div>
                            <div className="font-semibold text-gray-800">GlobalPay Direct</div>
                            <div className="text-sm text-gray-500">Payment Processing</div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                        <div className="flex items-center space-x-2">
                          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-green-700 font-semibold">Payment Successful</span>
                        </div>
                        <div className="text-sm text-gray-600 mt-2">Transaction completed successfully</div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Amount</span>
                          <span className="text-gray-800 font-semibold">$1,234.56</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Date</span>
                          <span className="text-gray-800">Jan 23, 2025</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content Section */}
              <div className="order-1 lg:order-2">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
                  We are the trusted payment platform for individuals and businesses
                </h3>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  GlobalPay Direct provides secure, fast, and reliable payment solutions 
                  that empower businesses and individuals to transact seamlessly across borders. 
                  Our platform is designed with security and user experience at its core.
                </p>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  With years of experience in the financial technology industry, we've built 
                  a robust payment infrastructure that supports multiple payment methods, 
                  currencies, and regions worldwide.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Banking Solutions Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
              Banking Solutions without Borders
            </h2>
            <p className="text-xl text-gray-600 mb-12 leading-relaxed">
              We break down geographical barriers to provide seamless payment experiences 
              for businesses and individuals worldwide. Our global infrastructure ensures 
              fast, secure, and cost-effective transactions regardless of location.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <div className="text-4xl mb-4">🌍</div>
                <h4 className="font-semibold text-gray-800 mb-2">Global Reach</h4>
                <p className="text-gray-600 text-sm">
                  Accept payments from customers worldwide
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <div className="text-4xl mb-4">🔒</div>
                <h4 className="font-semibold text-gray-800 mb-2">Secure</h4>
                <p className="text-gray-600 text-sm">
                  Bank-level encryption and security protocols
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <div className="text-4xl mb-4">⚡</div>
                <h4 className="font-semibold text-gray-800 mb-2">Fast</h4>
                <p className="text-gray-600 text-sm">
                  Lightning-fast transaction processing
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 md:p-12">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-8 text-center">
                Get in Touch
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Address</h4>
                    <p className="text-gray-600">
                      Altas Palmas Drive<br />
                      Brownsville, TX, USA
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Email</h4>
                    <p className="text-gray-600">
                      <a href="mailto:contact@globalpaydirect.com" className="hover:text-blue-600 transition-colors">
                        contact@globalpaydirect.com
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
